import { useEffect, useState } from 'react';
// Types
import type { CaptionProps } from 'react-day-picker';
import { useDayPicker, useNavigation } from 'react-day-picker';

import dayjs from 'dayjs';

import Icon from '@/components/base/Icon';

type SelectMonthProps = Pick<CaptionProps, 'displayMonth'>;

export function SelectMonth({ displayMonth }: SelectMonthProps) {
    const { goToMonth } = useNavigation();
    const {
        fromDate,
        toDate,
        locale,
        formatters: { formatMonthCaption }
    } = useDayPicker();

    const getOptions = () => {
        const dropdownMonths: { value: number; label: string }[] = [];

        if (fromDate && dayjs(fromDate).isSame(displayMonth, 'year')) {
            // only display the months included in the range
            const date = dayjs(fromDate).startOf('month');
            const endMonth =
                toDate && dayjs(fromDate).isSame(toDate, 'year') ? toDate.getMonth() : 11;
            for (let month = fromDate.getMonth(); month <= endMonth; month++) {
                const dateToInsert = dayjs(date).set('month', month);
                dropdownMonths.push({
                    value: dateToInsert.month(),
                    label: formatMonthCaption(dateToInsert.toDate(), { locale }) as string
                });
            }

            return dropdownMonths;
        }

        if (toDate && dayjs(toDate).isSame(displayMonth, 'year')) {
            // only display the months included in the range
            const date = dayjs(toDate).startOf('month');
            for (let month = 0; month <= toDate.getMonth(); month++) {
                const dateToInsert = dayjs(date).set('month', month);
                dropdownMonths.push({
                    value: dateToInsert.month(),
                    label: formatMonthCaption(dateToInsert.toDate(), { locale }) as string
                });
            }

            return dropdownMonths;
        }

        // display all the 12 months
        const date = dayjs().startOf('month'); // Any date should be OK, as we just need the year
        for (let month = 0; month <= 11; month++) {
            const dateToInsert = dayjs(date).set('month', month);
            dropdownMonths.push({
                value: dateToInsert.month(),
                label: formatMonthCaption(dateToInsert.toDate(), { locale }) as string
            });
        }

        return dropdownMonths;
    };

    const [options, setOptions] = useState(getOptions());

    useEffect(() => {
        setOptions(getOptions());
    }, [displayMonth]);

    const handleChange: React.ChangeEventHandler<HTMLSelectElement> = (e) => {
        goToMonth(dayjs(displayMonth).set('month', Number(e.target.value)).toDate());
    };

    return (
        <div className="relative flex">
            <select
                value={displayMonth.getMonth()}
                data-testid="datepicker-select-month"
                className="rdp-select_month border-0 bg-transparent p-0 pr-3 text-sm capitalize focus:ring-0 focus:ring-offset-0"
                onChange={handleChange}>
                {options.map((option) => {
                    return (
                        <option key={option.label} value={option.value}>
                            {option.label}
                        </option>
                    );
                })}
            </select>
            <Icon
                icon="chevron-bottom"
                className="absolute right-0 top-1/2 -translate-y-1/2 text-8 text-icoGray-400 dark:text-icoDarkMode-wolf"
            />
        </div>
    );
}
